import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { signIn, mapAuthError } from '../../../services/auth';
import { useAuth } from '../../../contexts/AuthContext';
import { DEFAULT_WORKSPACE_PATH, ONBOARDING_PATH } from '../../workspace/navigation';
import PhoneAuthPanel from '../components/PhoneAuthPanel';
import { ArrowRight } from 'lucide-react';

const Login = () => {
  const [method, setMethod] = useState('phone');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  const returnTo = location.state?.returnTo || DEFAULT_WORKSPACE_PATH;

  useEffect(() => {
    if (user && !authLoading) {
      navigate(returnTo, { replace: true });
    }
  }, [user, authLoading, navigate, returnTo]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(mapAuthError(err));
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-sand-100 px-4 py-12 font-body text-ink-900 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="mb-10 text-center">
          <Link to="/" className="group inline-flex items-center gap-3">
            <div className="h-11 w-11 overflow-hidden rounded-full ring-1 ring-ink-900/15 transition-transform group-hover:scale-105">
              <img
                src="/logo192.png"
                alt="Spirara Logo"
                className="h-full w-full scale-110 object-cover"
              />
            </div>
            <span className="font-display text-2xl font-light tracking-tight text-ink-900">
              Spirara<span className="text-saffron-500">.</span>
            </span>
          </Link>
        </div>

        <div className="mb-8 text-center">
          <h1 className="font-display text-4xl font-light leading-tight tracking-tight text-ink-900 sm:text-5xl">
            Welcome back
          </h1>
          <p className="mt-3 text-base leading-relaxed text-ink-700/80">
            Log in to your bookings, payments and calendar.
          </p>
        </div>

        <div className="bg-sand-50 p-8 shadow-[0_30px_60px_-30px_rgba(6,18,15,0.45)] ring-1 ring-ink-900/10 sm:p-10">
          <div className="mb-6 flex bg-white ring-1 ring-ink-900/10">
            {[
              { id: 'phone', label: 'Phone' },
              { id: 'email', label: 'Email' },
            ].map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  setMethod(item.id);
                  setError('');
                }}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  method === item.id
                    ? 'bg-ink-900 text-sand-50'
                    : 'text-ink-800 hover:bg-sand-100'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {method === 'phone' ? (
            <PhoneAuthPanel
              mode="login"
              submitLabel="Send code"
              onSuccess={({ isNewUser } = {}) =>
                navigate(isNewUser ? ONBOARDING_PATH : returnTo, { replace: true })
              }
            />
          ) : (
            <form className="space-y-6" onSubmit={handleEmailSubmit}>
              {error && (
                <div className="border border-red-300 bg-red-50 p-4">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="email"
                    className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700"
                  >
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="w-full bg-white px-4 py-3 text-ink-900 placeholder-ink-600/40 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700"
                  >
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    className="w-full bg-white px-4 py-3 text-ink-900 placeholder-ink-600/40 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex items-center justify-end">
                <Link
                  to="/reset-password"
                  className="text-sm font-medium text-ink-700 underline decoration-saffron-500 decoration-2 underline-offset-4 transition-colors hover:text-ink-900"
                >
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="group flex w-full items-center justify-center gap-2 bg-saffron-500 px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? (
                  'Logging in...'
                ) : (
                  <>
                    Log in
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-ink-700/80">
              Don&apos;t have an account?{' '}
              <Link
                to="/signup"
                className="font-semibold text-ink-900 underline decoration-saffron-500 decoration-2 underline-offset-4 transition-colors hover:text-ink-700"
              >
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
